import Divider from '@material-ui/core/Divider';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import ArrowBack from '@material-ui/icons/ArrowBack';
import EditIcon from '@material-ui/icons/Edit';

import { useStepperContext } from '../hooks/useStepper';
import { VaccineIcon } from '../../../assets/icons';
import { dayjs } from '../../../utils/functions/date';
import useStyles from '../styles';
import { useCallback } from 'react';
import {
  UserVaccination,
  TransportType,
  LocationType,
  PostUserVaccination,
  VaccinationStatusType,
} from '../../../services/models/vaccination';
import { VaccineValues } from '../validation';
import { VaccinationService } from '../../../services';

const vehicles: Record<TransportType, string> = {
  [TransportType.OwnVehicle]: 'Condução Própria',
  [TransportType.AppOrTaxi]: 'Aplicativo ou Táxi',
  [TransportType.PublicTransportation]: 'Transporte Público',
};

interface VaccineFourthStepProps {
  info?: UserVaccination;
}

function VaccineFourthStep({ info }: VaccineFourthStepProps) {
  const classes = useStyles();
  const { handlePrev, handleNext, changePage, data } =
    useStepperContext<VaccineValues>();

  const handleSubmit = useCallback(async () => {
    try {
      const { hour, date, ...rest } = data;

      const payload: PostUserVaccination = {
        scheduledDate: hour,
        ...rest,
      };

      const res = await VaccinationService.postUserVaccination(payload);
      if (res.data.status === VaccinationStatusType.Scheduled) {
        handleNext();
      }
    } catch (error) {
      console.error(error);
    }
  }, [data, handleNext]);

  return (
    <Grid container direction="column">
      <Grid item>
        <Box px={2} className={classes.disclaimer}>
          <img src={VaccineIcon} alt="vaccine-icon" />
          <Typography variant="h1">Resumo do Agendamento</Typography>
          <Typography>
            Verifique as informações para concluir seu agendamento.
          </Typography>
        </Box>
      </Grid>
      <Box px={{ xs: 0, md: 2 }}>
        <Grid spacing={2} container alignItems="center">
          <Grid item xs>
            <Typography variant="h6" className={classes.overline}>
              Data e Local
            </Typography>
          </Grid>
          <Grid item>
            <IconButton
              size="small"
              color="primary"
              aria-label="Editar Data e Local"
              onClick={() => changePage(1)}
            >
              <EditIcon />
            </IconButton>
          </Grid>
        </Grid>
        <Grid spacing={2} container>
          <Grid item xs>
            <Typography
              className={classes.overline}
              color="textSecondary"
              variant="overline"
            >
              Dia da Semana
            </Typography>
            <Typography className={classes.date}>
              {dayjs(data?.date).format('dddd')}
            </Typography>
          </Grid>
          <Grid item xs>
            <Typography
              className={classes.overline}
              color="textSecondary"
              variant="overline"
            >
              Data
            </Typography>
            <Typography>{dayjs(data?.date).format('DD/MM/YYYY')}</Typography>
          </Grid>
          <Grid item xs>
            <Typography
              className={classes.overline}
              color="textSecondary"
              variant="overline"
            >
              Horário
            </Typography>
            <Typography>{dayjs(data?.hour).format('HH:mm')}</Typography>
          </Grid>
        </Grid>
        {info?.locationType === LocationType.WorkPlace && (
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <Typography
                className={classes.overline}
                color="textSecondary"
                variant="overline"
              >
                Cidade
              </Typography>
              <Typography>{info.workplace?.city}</Typography>
            </Grid>
            <Grid item xs>
              <Typography
                className={classes.overline}
                color="textSecondary"
                variant="overline"
              >
                Descrição
              </Typography>
              <Typography className={classes.date}>
                {info.workplace?.description.toLowerCase()}
              </Typography>
            </Grid>
          </Grid>
        )}
        {info?.locationType === LocationType.AccreditedNetwork && (
          <>
            <Grid container spacing={2}>
              <Grid item xs>
                <Typography
                  className={classes.overline}
                  color="textSecondary"
                  variant="overline"
                >
                  Estado
                </Typography>
                <Typography className={classes.date}>
                  {info?.vaccinationPoint?.state.toLowerCase()}
                </Typography>
              </Grid>
              <Grid item xs>
                <Typography
                  className={classes.overline}
                  color="textSecondary"
                  variant="overline"
                >
                  Cidade
                </Typography>
                <Typography>{info?.vaccinationPoint?.city}</Typography>
              </Grid>
              <Grid item xs>
                <Typography
                  className={classes.overline}
                  color="textSecondary"
                  variant="overline"
                >
                  Bairro
                </Typography>
                <Typography>{info.vaccinationPoint?.neighborhood}</Typography>
              </Grid>
            </Grid>
            <Grid container spacing={2}>
              <Grid item xs={8}>
                <Typography
                  className={classes.overline}
                  color="textSecondary"
                  variant="overline"
                >
                  Endereço
                </Typography>
                <Typography>{info?.vaccinationPoint?.address}</Typography>
              </Grid>
              <Grid item xs>
                <Typography
                  className={classes.overline}
                  color="textSecondary"
                  variant="overline"
                >
                  Descrição
                </Typography>
                <Typography className={classes.date}>
                  {info.vaccinationPoint?.description.toLowerCase()}
                </Typography>
              </Grid>
            </Grid>
          </>
        )}
        <Box py={2}>
          <Divider />
        </Box>
        <Grid spacing={2} container alignItems="center">
          <Grid item xs>
            <Typography variant="h6" className={classes.overline}>
              Meio de Transporte
            </Typography>
          </Grid>
          <Grid item>
            <IconButton
              size="small"
              color="primary"
              aria-label="Editar Meio de Transporte"
              onClick={() => changePage(2)}
            >
              <EditIcon />
            </IconButton>
          </Grid>
        </Grid>
        <Grid spacing={2} container>
          {data?.transportType && (
            <Grid item xs>
              <Typography
                className={classes.overline}
                color="textSecondary"
                variant="overline"
              >
                Transporte
              </Typography>
              <Typography>{vehicles[data.transportType]}</Typography>
            </Grid>
          )}
          {data?.vehicleId && (
            <Grid item xs>
              <Typography
                className={classes.overline}
                color="textSecondary"
                variant="overline"
              >
                Placa do veículo
              </Typography>
              <Typography>{data?.vehicleId}</Typography>
            </Grid>
          )}
        </Grid>
      </Box>
      <Box py={3}>
        <Grid container justify="center">
          <Grid item>
            <Button
              startIcon={<ArrowBack />}
              variant="text"
              color="primary"
              className={classes.button}
              onClick={handlePrev}
            >
              Voltar
            </Button>{' '}
            <Button
              variant="contained"
              color="primary"
              disableElevation
              className={classes.button}
              onClick={handleSubmit}
            >
              Concluir
            </Button>
          </Grid>
        </Grid>
      </Box>
    </Grid>
  );
}

export default VaccineFourthStep;
